import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

const AdminPages = () => {
  const [activeTab, setActiveTab] = useState('emr');
  
  // EMR Integration state
  const [emrSystem, setEmrSystem] = useState('Epic');
  const [integrationType, setIntegrationType] = useState('FHIR API');
  const [apiEndpoint, setApiEndpoint] = useState('https://fhir-api-prod.epicsandbox.com/api/FHIR/R4/');
  const [clientId, setClientId] = useState('oncoCatalyst_app_client');
  const [clientSecret, setClientSecret] = useState('••••••••••••••••••');
  const [syncStatus, setSyncStatus] = useState({
    active: true,
    lastSync: new Date(Date.now() - 1000 * 60 * 60).toLocaleTimeString() // 1 hour ago
  });

  // Security Settings state
  const [dataResidency, setDataResidency] = useState({
    enabled: true,
    location: 'EU (Germany)'
  });
  const [gdprMode, setGdprMode] = useState(true);
  const [mfaRequired, setMfaRequired] = useState({
    enabled: true,
    scope: 'All Users'
  });
  const [sessionTimeout, setSessionTimeout] = useState(15);
  const [rbacEnabled, setRbacEnabled] = useState(true);
  const [securityScanDate, setSecurityScanDate] = useState('Today');

  // AI Agent Settings state
  const [aiAgents, setAiAgents] = useState({
    knowledge: { enabled: true },
    documentation: { enabled: true }
  });
  const [documentationSettings, setDocumentationSettings] = useState({
    learningMode: 'Adaptive (learns from your documentation patterns)',
    suggestionFrequency: 'Low (suggestions at end of note)',
    activeTemplate: 'Oncology Follow-up Visit'
  });

  // Federated Learning state
  const [federatedLearning, setFederatedLearning] = useState({
    active: true,
    lastUpdate: 'Yesterday',
    models: [
      { name: 'Documentation Assistant', status: 'Active' },
      { name: 'Knowledge Graph Enhancer', status: 'Active' },
      { name: 'Literature Relevance', status: 'Active' },
      { name: 'Patient Education', status: 'Pending' }
    ],
    stats: {
      hospitals: 15,
      departments: 42,
      physicians: 187,
      cases: 8200
    }
  });

  // Handle EMR Integration actions
  const handleEMRSync = () => {
    toast.info("Syncing with EMR...");
    
    // Simulate a sync operation
    setTimeout(() => {
      setSyncStatus({
        active: true,
        lastSync: new Date().toLocaleTimeString()
      });
      toast.success("EMR sync completed successfully");
    }, 2000);
  };

  const handleEMRSave = () => {
    toast.success("EMR integration settings saved");
  };

  // Handle Security actions
  const handleSecurityScan = () => {
    toast.info("Running security scan...");
    
    // Simulate a scan operation
    setTimeout(() => {
      setSecurityScanDate('Just now');
      toast.success("Security scan completed with no issues found");
    }, 2500);
  };

  const toggleDataResidency = () => {
    setDataResidency({
      ...dataResidency,
      enabled: !dataResidency.enabled
    });
    
    toast.info(`Data residency ${!dataResidency.enabled ? 'enabled' : 'disabled'}`);
  };

  const toggleGDPR = () => {
    setGdprMode(!gdprMode);
    toast.info(`GDPR compliance mode ${!gdprMode ? 'enabled' : 'disabled'}`);
  };

  const toggleMFA = () => {
    setMfaRequired({
      ...mfaRequired,
      enabled: !mfaRequired.enabled
    });
    
    toast.info(`Multi-factor authentication ${!mfaRequired.enabled ? 'enabled' : 'disabled'}`);
  };

  const configureRBAC = () => {
    toast.info("Opening RBAC configuration...");
    // In a real app, this would open a modal with RBAC options
  };

  // Handle AI Agent actions
  const toggleAIAgent = (agent) => {
    setAiAgents({
      ...aiAgents,
      [agent]: { enabled: !aiAgents[agent].enabled }
    });
    
    toast.info(`${agent.charAt(0).toUpperCase() + agent.slice(1)} Agent ${!aiAgents[agent].enabled ? 'enabled' : 'disabled'}`);
  };

  const configureAgent = (agent) => {
    toast.info(`Opening configuration for ${agent} Agent`);
    // In a real app, this would open a configuration modal
  };

  const handleLearningModeChange = (e) => {
    setDocumentationSettings({
      ...documentationSettings,
      learningMode: e.target.value
    });
    
    toast.info(`Learning mode changed to ${e.target.value}`);
  };

  const handleSuggestionFrequencyChange = (e) => {
    setDocumentationSettings({
      ...documentationSettings,
      suggestionFrequency: e.target.value
    });
    
    toast.info(`Suggestion frequency changed to ${e.target.value}`);
  };

  const handleTemplateChange = (e) => {
    setDocumentationSettings({
      ...documentationSettings,
      activeTemplate: e.target.value
    });
  };

  const editTemplates = () => {
    toast.info("Opening template editor...");
    // In a real app, this would open a template editor
  };

  // Handle Federated Learning actions
  const updateFederatedLearning = () => {
    toast.info("Updating federated learning models...");
    
    // Simulate an update operation
    setTimeout(() => {
      setFederatedLearning({
        ...federatedLearning,
        lastUpdate: 'Just now'
      });
      toast.success("Federated learning models updated successfully");
    }, 3000);
  };

  const runTrainingCycle = () => {
    toast.info("Starting training cycle. This may take a while...");
    // In a real app, this would initiate a training cycle
  };

  const networkSettings = () => {
    toast.info("Opening network settings...");
    // In a real app, this would open network settings
  };

  const generateReports = () => {
    toast.info("Generating federated learning reports...");
    
    // Simulate report generation
    setTimeout(() => {
      toast.success("Reports generated and ready for download");
    }, 2000);
  };

  const renderEMRIntegration = () => (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">EMR Integration Configuration</h2>
      <p className="text-gray-600 mb-4">Configure OncoCatalyst's connection with your existing EMR system</p>
     
      <div className="bg-green-100 border border-green-200 rounded-lg p-4 flex justify-between items-center mb-6">
        <div className="flex items-center">
          <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white mr-3">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <span className="font-medium text-green-700">Integration Active</span>
            <span className="ml-8 text-gray-600">Current EMR: {emrSystem} (v2023.2)</span>
            <span className="ml-8 text-gray-600">Last sync: {syncStatus.lastSync}</span>
          </div>
        </div>
        <button 
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          onClick={handleEMRSync}
        >
          Sync Now
        </button>
      </div>
     
      <div className="grid grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">EMR System</label>
          <select 
            className="w-full p-2 border rounded"
            value={emrSystem}
            onChange={(e) => setEmrSystem(e.target.value)}
          >
            <option>Epic</option>
            <option>Cerner</option>
            <option>Allscripts</option>
            <option>NextGen</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Integration Type</label>
          <select 
            className="w-full p-2 border rounded"
            value={integrationType}
            onChange={(e) => setIntegrationType(e.target.value)}
          >
            <option>FHIR API</option>
            <option>HL7</option>
            <option>Direct Database</option>
            <option>Custom</option>
          </select>
        </div>
      </div>
     
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h3 className="text-lg font-medium mb-4">Connection Details</h3>
       
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">API Endpoint URL</label>
          <input 
            type="text" 
            className="w-full p-2 border rounded" 
            value={apiEndpoint}
            onChange={(e) => setApiEndpoint(e.target.value)}
          />
        </div>
       
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
            <input 
              type="text" 
              className="w-full p-2 border rounded" 
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client Secret</label>
            <input 
              type="password" 
              className="w-full p-2 border rounded" 
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
            />
          </div>
        </div>
      </div>
     
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Data Mapping</h3>
          <button 
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={handleEMRSave}
          >
            Save Configuration
          </button>
        </div>
       
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">OncoCatalyst Field</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Epic Field</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sync Direction</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            <tr>
              <td className="px-6 py-4 whitespace-nowrap">Patient Demographics</td>
              <td className="px-6 py-4 whitespace-nowrap">Patient Demographics</td>
              <td className="px-6 py-4 whitespace-nowrap">Bidirectional</td>
            </tr>
            <tr>
              <td className="px-6 py-4 whitespace-nowrap">Clinical Documents</td>
              <td className="px-6 py-4 whitespace-nowrap">Clinical Document</td>
              <td className="px-6 py-4 whitespace-nowrap">OncoCatalyst → Epic</td>
            </tr>
            <tr>
              <td className="px-6 py-4 whitespace-nowrap">
                <button className="text-blue-500">+ Add Field Mapping</button>
              </td>
              <td className="px-6 py-4 whitespace-nowrap"></td>
              <td className="px-6 py-4 whitespace-nowrap"></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
 
  const renderSecuritySettings = () => (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Security & Compliance Controls</h2>
      <p className="text-gray-600 mb-4">Configure privacy, security, and compliance settings</p>
     
      <div className="bg-green-100 border border-green-200 rounded-lg p-4 flex justify-between items-center mb-6">
        <div className="flex items-center">
          <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white mr-3">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
          <span className="font-medium text-green-700">All Security Controls Active</span>
          <span className="ml-8 text-gray-600">Last security scan: {securityScanDate}</span>
        </div>
        <button 
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          onClick={handleSecurityScan}
        >
          Run Scan
        </button>
      </div>
     
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h3 className="text-lg font-medium mb-4">Data Privacy Controls</h3>
       
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span>Data Residency</span>
            <div className="flex items-center">
              <div 
                className={`relative inline-block w-16 h-8 mr-4 ${dataResidency.enabled ? 'bg-green-500' : 'bg-gray-400'} rounded-full cursor-pointer`}
                onClick={toggleDataResidency}
              >
                <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-medium">
                  {dataResidency.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <span>{dataResidency.enabled ? dataResidency.location : 'N/A'}</span>
            </div>
          </div>
         
          <div className="flex justify-between items-center">
            <span>GDPR Compliance Mode</span>
            <div className="flex items-center">
              <div 
                className={`relative inline-block w-16 h-8 mr-2 ${gdprMode ? 'bg-green-500' : 'bg-gray-400'} rounded-full cursor-pointer`}
                onClick={toggleGDPR}
              >
                <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-medium">
                  {gdprMode ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
     
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h3 className="text-lg font-medium mb-4">Authentication & Access Controls</h3>
       
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span>Multi-Factor Authentication</span>
            <div className="flex items-center">
              <div 
                className={`relative inline-block w-16 h-8 mr-4 ${mfaRequired.enabled ? 'bg-green-500' : 'bg-gray-400'} rounded-full cursor-pointer`}
                onClick={toggleMFA}
              >
                <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-medium">
                  {mfaRequired.enabled ? 'Required' : 'Optional'}
                </span>
              </div>
              <span>{mfaRequired.enabled ? mfaRequired.scope : 'None'}</span>
            </div>
          </div>
         
          <div className="flex justify-between items-center">
            <span>Session Timeout</span>
            <div className="flex items-center">
              <input 
                type="number" 
                className="w-16 p-2 border rounded mr-2" 
                value={sessionTimeout}
                onChange={(e) => setSessionTimeout(parseInt(e.target.value))}
                min="1"
                max="60"
              />
              <span>minutes</span>
            </div>
          </div>
         
          <div className="flex justify-between items-center">
            <span>Role-Based Access Control</span>
            <div className="flex items-center">
              <div 
                className={`relative inline-block w-16 h-8 mr-2 ${rbacEnabled ? 'bg-green-500' : 'bg-gray-400'} rounded-full cursor-pointer`}
                onClick={() => setRbacEnabled(!rbacEnabled)}
              >
                <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-medium">
                  {rbacEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <button 
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={configureRBAC}
              >
                Configure
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
 
  const renderAISettings = () => (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">AI Agent Configuration</h2>
      <p className="text-gray-600 mb-4">Customize how the AI agents behave and interact with your workflow</p>
     
      <h3 className="text-xl font-medium mb-4">Active AI Agents</h3>
     
      <div className="space-y-4 mb-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-full bg-blue-400 flex items-center justify-center text-white text-lg font-bold mr-4">
                K
              </div>
              <div>
                <h4 className="text-lg font-medium">Knowledge Navigator Agent</h4>
                <p className="text-gray-600">Traverses the oncology knowledge graph to surface relevant information</p>
              </div>
            </div>
            <div className="flex items-center">
              <div 
                className={`relative inline-block w-16 h-8 mr-4 ${aiAgents.knowledge.enabled ? 'bg-green-500' : 'bg-gray-400'} rounded-full cursor-pointer`}
                onClick={() => toggleAIAgent('knowledge')}
              >
                <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-medium">
                  {aiAgents.knowledge.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <button 
                className="px-4 py-2 border border-blue-500 text-blue-500 rounded hover:bg-blue-50"
                onClick={() => configureAgent('Knowledge Navigator')}
              >
                Configure
              </button>
            </div>
          </div>
        </div>
       
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center text-white text-lg font-bold mr-4">
                D
              </div>
              <div>
                <h4 className="text-lg font-medium">Documentation Intelligence Agent</h4>
                <p className="text-gray-600">Assists with clinical documentation and learns from your note patterns</p>
              </div>
            </div>
            <div className="flex items-center">
              <div 
                className={`relative inline-block w-16 h-8 mr-4 ${aiAgents.documentation.enabled ? 'bg-green-500' : 'bg-gray-400'} rounded-full cursor-pointer`}
                onClick={() => toggleAIAgent('documentation')}
              >
                <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-medium">
                  {aiAgents.documentation.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <button 
                className="px-4 py-2 border border-blue-500 text-blue-500 rounded hover:bg-blue-50"
                onClick={() => configureAgent('Documentation Intelligence')}
              >
                Configure
              </button>
            </div>
          </div>
        </div>
      </div>
     
      <div className="bg-white p-6 rounded-lg shadow mb-6 border-l-4 border-purple-500">
        <h3 className="text-lg font-medium mb-4">Documentation Intelligence Agent Configuration</h3>
       
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Learning Mode</label>
            <select 
              className="w-full p-2 border rounded"
              value={documentationSettings.learningMode}
              onChange={handleLearningModeChange}
            >
              <option>Adaptive (learns from your documentation patterns)</option>
              <option>Standard (uses built-in templates only)</option>
              <option>Minimal (suggestions only when requested)</option>
            </select>
          </div>
         
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Suggestion Frequency</label>
            <select 
              className="w-full p-2 border rounded"
              value={documentationSettings.suggestionFrequency}
              onChange={handleSuggestionFrequencyChange}
            >
              <option>Low (suggestions at end of note)</option>
              <option>Medium (suggestions after each section)</option>
              <option>High (real-time suggestions)</option>
            </select>
          </div>
         
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Note Templates</label>
            <div className="flex items-center">
              <select 
                className="flex-1 p-2 border rounded"
                value={documentationSettings.activeTemplate}
                onChange={handleTemplateChange}
              >
                <option>Oncology Follow-up Visit</option>
                <option>New Patient Consultation</option>
                <option>Treatment Plan</option>
                <option>Chemotherapy Order</option>
              </select>
              <button 
                className="ml-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={editTemplates}
              >
                Edit Templates
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
 
  const renderFederatedLearning = () => (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Federated Learning Dashboard</h2>
      <p className="text-gray-600 mb-4">Manage privacy-preserving learning across the OncoCatalyst network</p>
     
      <div className="bg-green-100 border border-green-200 rounded-lg p-4 flex justify-between items-center mb-6">
        <div className="flex items-center">
          <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white mr-3">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
          <span className="font-medium text-green-700">Federated Learning Active</span>
          <span className="ml-8 text-gray-600">Last model update: {federatedLearning.lastUpdate}</span>
        </div>
        <button 
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          onClick={updateFederatedLearning}
        >
          Update Now
        </button>
      </div>
     
      <div className="grid grid-cols-2 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium mb-4">Learning Models Status</h3>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Model</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {federatedLearning.models.map((model, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap">{model.name}</td>
                  <td className={`px-6 py-4 whitespace-nowrap ${model.status === 'Active' ? 'text-green-500' : 'text-orange-500'}`}>
                    {model.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
       
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium mb-4">Model Performance</h3>
          <div className="h-48 relative">
            {/* Simple chart showing upward trend */}
            <svg viewBox="0 0 100 40" className="w-full h-full">
              <path d="M0,35 L20,30 L40,25 L60,18 L80,12 L100,5" stroke="#3b82f6" strokeWidth="2" fill="none" />
            </svg>
            <div className="absolute bottom-0 left-0 text-sm text-gray-500">Jan</div>
            <div className="absolute bottom-0 right-0 text-sm text-gray-500">Apr</div>
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 text-sm text-gray-500">Model Accuracy Improvement</div>
            <div className="absolute top-4 right-4 text-sm text-green-500">+12.4% since federated learning enabled</div>
          </div>
        </div>
      </div>
     
      <h3 className="text-xl font-medium mb-4">Network Participation</h3>
      <div className="grid grid-cols-4 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg shadow text-center">
          <div className="text-4xl font-bold text-blue-500 mb-2">{federatedLearning.stats.hospitals}</div>
          <div className="text-gray-600">Participating Hospitals</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow text-center">
          <div className="text-4xl font-bold text-blue-500 mb-2">{federatedLearning.stats.departments}</div>
          <div className="text-gray-600">Oncology Departments</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow text-center">
          <div className="text-4xl font-bold text-blue-500 mb-2">{federatedLearning.stats.physicians}</div>
          <div className="text-gray-600">Contributing Physicians</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow text-center">
          <div className="text-4xl font-bold text-blue-500 mb-2">{(federatedLearning.stats.cases / 1000).toFixed(1)}k</div>
          <div className="text-gray-600">Case Studies Shared</div>
        </div>
      </div>
     
      <div className="flex justify-between">
        <button 
          className="px-5 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          onClick={runTrainingCycle}
        >
          Run Training Cycle
        </button>
        <button 
          className="px-5 py-2 border border-gray-300 rounded hover:bg-gray-50"
          onClick={networkSettings}
        >
          Network Settings
        </button>
        <button 
          className="px-5 py-2 border border-gray-300 rounded hover:bg-gray-50"
          onClick={generateReports}
        >
          Generate Reports
        </button>
      </div>
    </div>
  );
 
  const renderContent = () => {
    switch(activeTab) {
      case 'emr':
        return renderEMRIntegration();
      case 'security':
        return renderSecuritySettings();
      case 'ai':
        return renderAISettings();
      case 'federated':
        return renderFederatedLearning();
      default:
        return renderEMRIntegration();
    }
  };
 
  return (
    <div className="min-h-screen bg-gray-100">
      <div className="flex items-center justify-between w-full bg-gray-800 text-white p-3">
        <Link to="/" className="text-xl font-bold hover:text-blue-300 transition-colors">
          OncoCatalyst
        </Link>
        <div className="flex-1 mx-4 text-right">
          {activeTab === 'emr' && 'Admin: EMR Integration'}
          {activeTab === 'security' && 'Admin: Security Controls'}
          {activeTab === 'ai' && 'AI Agent Configuration'}
          {activeTab === 'federated' && 'Admin: Federated Learning'}
        </div>
        <div className="flex items-center">
          <div className="relative mr-3">
            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
              {activeTab === 'emr' || activeTab === 'federated' ? '1' : '2'}
            </div>
          </div>
          <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
            SY
          </div>
        </div>
      </div>
     
      <div className="p-4">
        <div className="flex border-b mb-4">
          <button
            className={`py-2 px-4 mr-2 ${activeTab === 'emr' ? 'border-b-2 border-blue-500 font-medium' : 'text-gray-600'}`}
            onClick={() => setActiveTab('emr')}
          >
            EMR Integration
          </button>
          <button
            className={`py-2 px-4 mr-2 ${activeTab === 'security' ? 'border-b-2 border-blue-500 font-medium' : 'text-gray-600'}`}
            onClick={() => setActiveTab('security')}
          >
            Security Settings
          </button>
          <button
            className={`py-2 px-4 mr-2 ${activeTab === 'ai' ? 'border-b-2 border-blue-500 font-medium' : 'text-gray-600'}`}
            onClick={() => setActiveTab('ai')}
          >
            AI Agent Settings
          </button>
          <button
            className={`py-2 px-4 ${activeTab === 'federated' ? 'border-b-2 border-blue-500 font-medium' : 'text-gray-600'}`}
            onClick={() => setActiveTab('federated')}
          >
            Federated Learning
          </button>
        </div>
       
        {renderContent()}
      </div>
    </div>
  );
};

export default AdminPages;